import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { SaveAlt as SaveAltIcon } from '@mui/icons-material';
// Reuse the same helpers your tables use
import { isSystemId, resolveSystemValue } from '../../utils/systemColumns';
import {
  unwrapValue,
  getLinkedForRow,
  extractFirstAssetId,
  parseVirtualSpec,
} from '../../utils/assetCellHelpers';
import {
  getProviderColumnExportValue,
} from '../../utils/providerOutputValueUtils';
import {
  getProviderColumnDefinition,
} from '../../integrations/providerColumns';

/**
 * ExportOptions Component
 * - UI matches your existing version (IconButton, Tooltip, Menu)
 * - Supports CSV and Excel (.xlsx) export
 * - Added `columnOrder` logic to maintain the correct column sequence
 * - Extensive console logs for easier debugging
 * - 🆕 Dynamic import for XLSX to improve build performance and reduce bundle size
 */
const ExportOptions = ({ data = [], columns = [], columnOrder = [] }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  // Open/Close Menu Handlers
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // Helper: Prepare ordered data based on `columnOrder`, resolving values like the table
  const prepareOrderedData = () => {
    // 1) Respect ColumnDraftManager’s ordering & visibility
    const orderedColumns = (columnOrder || [])
      .filter(col => col && col.visible !== false)
      .map(orderCol => {
        const orderColId =
          typeof orderCol === 'string'
            ? orderCol
            : orderCol.id;

        const matchedColumn = columns.find(c => c.id === orderColId);

        return matchedColumn || (
          typeof orderCol === 'string'
            ? { id: orderCol, label: orderCol, visible: true }
            : orderCol
        );
      });

    const headers = orderedColumns.map(col => col.label || col.id);

    // 2) Column-aware cell resolver
    const getExportValue = (col, row) => {
      if (!row) return '';

      // --- System columns (ids like 'sys:*') ---
      if (isSystemId(String(col.id)) || col.kind === 'system') {
        let v = resolveSystemValue(row, col.id);
        // Firestore Timestamp → Date
        if (v && typeof v.toDate === 'function') v = v.toDate();
        // Format submittedAt nicely; others stringify
        if (String(col.id) === 'sys:submittedAt' && v) {
          return new Date(v).toLocaleString();
        }
        return v == null ? '' : String(v);
      }

      // --- Virtual columns (thumb/version/assetType/aufField/assetId/provider output) ---
      const spec = parseVirtualSpec(col); // works with either object {role, ufi, aufUfi} or id string pattern
      if (spec && spec.role) {
        // Provider-owned virtual columns:
        // - Bitly Short URL today
        // - Future Bitly metrics / HubSpot / Slack / BigQuery outputs later
        //
        // Enterprise note:
        // This exports already-fetched row data only. It does not call provider
        // APIs, add listeners, or hard-code provider row paths into the exporter.
        if (spec.role === 'providerOutput' || spec.role === 'providerMetric') {
          const providerColumn = {
            ...col,
            ...spec,
            kind: col.kind || 'virtual',
          };

          const definition = getProviderColumnDefinition(providerColumn);

          return getProviderColumnExportValue(
            row,
            providerColumn,
            definition
          );
        }

        const linked = getLinkedForRow(row); // Map<attachUfi, entry> | null
        const linkedEntry = spec.ufi && linked ? linked.get(spec.ufi) : null;

        switch (spec.role) {
          case 'thumb': {
            // For CSV, export the first assetId instead of a URL
            return spec.ufi ? (extractFirstAssetId(row, spec.ufi) || '') : '';
          }
          case 'version': {
            if (linkedEntry && typeof linkedEntry.versionNumber !== 'undefined') {
              return String(linkedEntry.versionNumber ?? '');
            }
            // fallback to payload
            const cell = row?.responses?.[spec.ufi];
            const x    = unwrapValue(cell && typeof cell === 'object' && 'value' in cell ? cell.value : cell);
            if (Array.isArray(x)) {
              const first = x.find(it => it && typeof it === 'object' && (it.assetId || it.id));
              return String(first?.versionNumber ?? first?.version ?? '');
            }
            if (x && typeof x === 'object') {
              return String(x.versionNumber ?? x.version ?? '');
            }
            return '';
          }
          case 'assetType': {
            if (linkedEntry && typeof linkedEntry.assetType !== 'undefined') {
              return String(linkedEntry.assetType ?? '');
            }
            const cell = row?.responses?.[spec.ufi];
            const x    = unwrapValue(cell && typeof cell === 'object' && 'value' in cell ? cell.value : cell);
            if (Array.isArray(x)) {
              const first = x.find(it => it && typeof it === 'object' && (it.assetId || it.id));
              return String(first?.assetType ?? '');
            }
            if (x && typeof x === 'object') {
              return String(x.assetType ?? '');
            }
            return '';
          }
          case 'aufField': {
            // prefer linked AUF responses
            if (linkedEntry && linkedEntry.aufResponses && spec.aufUfi in linkedEntry.aufResponses) {
              const raw = linkedEntry.aufResponses[spec.aufUfi];
              if (Array.isArray(raw)) return raw.filter(Boolean).join(', ');
              return raw == null ? '' : String(raw);
            }
            // no preview-meta fallback in exporter – keep simple/accurate
            return '';
          }
          case 'assetId': {
            return spec.ufi ? (extractFirstAssetId(row, spec.ufi) || '') : '';
          }
          default:
            return '';
        }
      }

      // --- Base (regular UFI) fields ---
      const rawCell = row?.responses?.[col.id];
      const rawVal  = (rawCell && typeof rawCell === 'object' && 'value' in rawCell) ? rawCell.value : rawCell;
      const val     = unwrapValue(rawVal);

      // arrays → join
      if (Array.isArray(val)) {
        return val
          .map(item => {
            const u = unwrapValue(item);
            if (u && typeof u === 'object') {
              return u.assetId || u.id || (typeof u.value !== 'object' ? u.value : '');
            }
            return u == null ? '' : String(u);
          })
          .filter(Boolean)
          .join(', ');
      }

      // objects → prefer assetId/id/value, else empty
      if (val && typeof val === 'object') {
        if (val.assetId || val.id) return val.assetId || val.id;
        if (typeof val.value !== 'object' && val.value != null) return String(val.value);
        return '';
      }

      // primitive or null/undefined
      return val == null ? '' : String(val);
    };

    const rows = (data || []).map(row =>
      orderedColumns.map(col => getExportValue(col, row))
    );

    return { headers, rows };
  };


  // ================= CSV Export =================
  const exportToCSV = () => {
    console.group("[ExportOptions] CSV Export Process");

    const { headers, rows } = prepareOrderedData();

    if (!rows.length) {
      console.error("[CSV Export] No valid rows generated for CSV.");
      console.groupEnd();
      return;
    }

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(value => `"${value.toString().replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "form_submissions.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log("[CSV Export] CSV file successfully exported.");
    console.groupEnd();
    handleClose();
  };

  // ================= Excel Export (.xlsx) =================
  const exportToExcel = async () => {
    console.group("[ExportOptions] Excel Export Process");

    try {
      // 🆕 Dynamic import for XLSX to reduce bundle size
      const XLSX = await import('xlsx');

      const { headers, rows } = prepareOrderedData();

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Submissions");

      // Create Excel File
      XLSX.writeFile(workbook, 'form_submissions.xlsx');

      console.log("[Excel Export] Excel file successfully exported.");
    } catch (error) {
      console.error("[Excel Export] Error loading XLSX library:", error);
    }

    console.groupEnd();
    handleClose();
  };

  return (
    <div>
      <Tooltip 
        title="Export Options" 
        componentsProps={{
          tooltip: { sx: { fontSize: '12px' } } // 🔹 Smaller tooltip text
        }}
      >
        <IconButton onClick={handleClick}>
          <SaveAltIcon />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem
          onClick={exportToCSV}
          sx={{ fontSize: '12px', padding: '6px 12px' }} // 🔹 Smaller menu text
        >
          Export to CSV
        </MenuItem>

        <MenuItem
          onClick={exportToExcel}
          sx={{ fontSize: '12px', padding: '6px 12px' }} // 🔹 Smaller menu text
        >
          Export to Excel (.xlsx)
        </MenuItem>
      </Menu>
    </div>
  );
};

export default ExportOptions;
